import { err, ok, type Result } from '@shared/result';
import { CalendarDate } from './CalendarDate';

/**
 * An inclusive span of calendar days. Both ends are part of the range: a
 * request for "10 – 21 ago" covers the 21st, which is what a person means when
 * they say it and what the design shows.
 */

export type DateRangeError =
  | { readonly kind: 'inverted'; readonly start: string; readonly end: string }
  | { readonly kind: 'too-long'; readonly naturalDays: number; readonly limit: number };

/**
 * A guard against absurd input rather than a policy: the longest legitimate
 * absence in the system is maternity leave at 84 natural days, and a mistyped
 * year would otherwise produce a range spanning decades and a calendar query
 * that tries to render 4,000 columns. Real limits (minimum notice, entitlement)
 * are policy and live elsewhere.
 */
const MAX_NATURAL_DAYS = 400;

export class DateRange {
  private constructor(
    readonly start: CalendarDate,
    readonly end: CalendarDate,
  ) {}

  static create(start: CalendarDate, end: CalendarDate): Result<DateRange, DateRangeError> {
    if (end.isBefore(start)) {
      return err({ kind: 'inverted', start: start.toISO(), end: end.toISO() });
    }
    const naturalDays = start.daysUntil(end) + 1;
    if (naturalDays > MAX_NATURAL_DAYS) {
      return err({ kind: 'too-long', naturalDays, limit: MAX_NATURAL_DAYS });
    }
    return ok(new DateRange(start, end));
  }

  static fromISO(startISO: string, endISO: string): Result<DateRange, DateRangeError | 'bad-date'> {
    const start = CalendarDate.fromISO(startISO);
    if (!start.ok) return err('bad-date');
    const end = CalendarDate.fromISO(endISO);
    if (!end.ok) return err('bad-date');
    return DateRange.create(start.value, end.value);
  }

  static fromISOOrThrow(startISO: string, endISO: string): DateRange {
    const range = DateRange.fromISO(startISO, endISO);
    if (!range.ok) throw new Error(`Not a range: ${startISO}..${endISO} (${JSON.stringify(range.error)})`);
    return range.value;
  }

  /** A range covering a single day. */
  static singleDay(day: CalendarDate): DateRange {
    return new DateRange(day, day);
  }

  /** Every day in the month containing `day`. */
  static month(day: CalendarDate): DateRange {
    return new DateRange(day.startOfMonth, day.endOfMonth);
  }

  /** Calendar days covered, both ends included. */
  get naturalDays(): number {
    return this.start.daysUntil(this.end) + 1;
  }

  contains(date: CalendarDate): boolean {
    return date.isSameOrAfter(this.start) && date.isSameOrBefore(this.end);
  }

  /**
   * True when the two ranges share at least one day. Inclusive on both ends —
   * a range ending the 10th overlaps one starting the 10th, because both
   * people are out that day, which is exactly what the concurrency cap cares
   * about.
   */
  overlaps(other: DateRange): boolean {
    return this.start.isSameOrBefore(other.end) && other.start.isSameOrBefore(this.end);
  }

  /** The shared span, or null when they do not touch. */
  intersection(other: DateRange): DateRange | null {
    if (!this.overlaps(other)) return null;
    return new DateRange(
      CalendarDate.max(this.start, other.start),
      CalendarDate.min(this.end, other.end),
    );
  }

  /** Every day in the range, in order. Bounded by `MAX_NATURAL_DAYS`. */
  *days(): Generator<CalendarDate, void, undefined> {
    let cursor = this.start;
    while (cursor.isSameOrBefore(this.end)) {
      yield cursor;
      cursor = cursor.addDays(1);
    }
  }

  toDayArray(): CalendarDate[] {
    return [...this.days()];
  }

  equals(other: DateRange): boolean {
    return this.start.equals(other.start) && this.end.equals(other.end);
  }

  toISO(): { readonly start: string; readonly end: string } {
    return { start: this.start.toISO(), end: this.end.toISO() };
  }

  toString(): string {
    return `${this.start.toISO()}..${this.end.toISO()}`;
  }
}
